<template>
  <StormFrequencySelector v-model="selectedFrequency" v-model:duration="selectedDuration" :report="report" />
  <p>
    This section presents the estimated stormwater runoff from the proposed development site under a
    {{ formattedDuration }} {{ selectedFrequency }}-year design storm. The goal is to quantify the volume and
    peak flow that must be managed by the stormwater system.
  </p>
  <h2>Site characteristics</h2>
  <p>
    The following properties define the site and its runoff potential. These characteristics are essential inputs for
    calculating stormwater volumes and flow rates.
  </p>
  <div class="calculation-row d--f ai--c fw--w">
    <IconListItem theme="primary" icon="ruler" title="Site area">
      <span class="unit-text">{{ number_with_delimiter(report.site_area) }} m<sup>2</sup></span>
    </IconListItem>
    <IconListItem theme="primary" icon="pie-chart" title="Pre-development">
      {{ Number(report.pre_development_impervious_percentage) }}%
    </IconListItem>
    <IconListItem theme="primary" icon="area" title="Paved area">
      <span class="unit-text">{{ number_with_delimiter(report.paved_area_m2) }} m<sup>2</sup></span>
    </IconListItem>
    <IconListItem theme="primary" icon="tachometer" title="Weighted coefficient">
      {{ getStormMetric("adjusted_coefficient") }}
    </IconListItem>
    <IconListItem theme="primary" icon="cloud-rain" title="Rainfall intensity">
      {{ getStormMetric("rainfall_intensity") }} mm/hr
    </IconListItem>
  </div>

  <RainfallDataTables
    v-if="report.idf_data"
    title="Rainfall Intensity (mm/hr)"
    field="rainfall_rate_mm_hr"
    :idf-data="report.idf_data"
  />

  <RainfallDataTables
    v-if="report.idf_data"
    title="Rainfall Depth (mm)"
    field="rainfall_mm"
    :idf-data="report.idf_data"
  />

  <h2 class="mt-20px">Peak flow</h2>
  <p>
    Peak flow represents the maximum rate at which water will run off the site during the design storm. This calculation
    uses the Rational Method formula (Q = 2.78 × C × I × A) and is critical for sizing stormwater conveyance systems.
  </p>
  <div class="calculation-row d--f ai--c fw--w">
    <IconListItem theme="outlined" icon="tachometer" title="Weighted coefficient">
      {{ getStormMetric("adjusted_coefficient") }}
    </IconListItem>
    <span class="operator-sign">×</span>
    <IconListItem theme="outlined" icon="cloud-rain" title="Rainfall intensity">
      {{ getStormMetric("rainfall_intensity_15min") }} mm/hr
    </IconListItem>
    <span class="operator-sign">×</span>
    <IconListItem theme="outlined" icon="ruler" title="Drainage area">
      {{ number_with_delimiter(report.site_area / 10000) }} ha
    </IconListItem>
    <span class="operator-sign">×</span>
    <span class="operator-sign">2.78</span>
    <span class="operator-sign">=</span>
    <IconListItem theme="primary" icon="water" title="Peak flow">
      {{ number_with_delimiter(Number(getStormMetric("original_peak_flow")).toFixed(0)) }} L/s
    </IconListItem>
  </div>

  <h2 class="mt-20px">Total runoff volume</h2>
  <p>
    The total runoff volume indicates how much water will need to be managed over the entire storm duration. This is
    calculated based on rainfall depth, site area, and impervious percentage. It helps determine how much temporary
    storage is required during a {{ selectedFrequency }}-year, {{ formattedDuration }} storm.
  </p>
  <div class="calculation-row d--f ai--c fw--w">
    <IconListItem theme="outlined" icon="cloud" title="Rainfall Depth">
      {{ getStormMetric("rainfall_depth") }} mm
    </IconListItem>
    <span class="operator-sign">×</span>
    <IconListItem theme="outlined" icon="ruler" title="Total Area">
      {{ number_with_delimiter(report.site_area) }} m<sup>2</sup>
    </IconListItem>
    <span class="operator-sign">×</span>
    <IconListItem theme="outlined" icon="pie-chart" title="Impervious %">
      {{ Number(report.pre_development_impervious_percentage) }}%
    </IconListItem>
    <span class="operator-sign">/</span>
    <span class="operator-sign">1000</span>
    <span class="operator-sign">=</span>
    <IconListItem theme="primary" icon="cube" :title="`Volume (${formattedDuration})`">
      {{ number_with_delimiter(Number(getStormMetric("total_runoff_volume")).toFixed(0)) }} m<sup>3</sup>
    </IconListItem>
  </div>
  <p class="mt-20px">
    The development is expected to generate a peak runoff rate of
    {{ number_with_delimiter(Number(getStormMetric("original_peak_flow")).toFixed(0)) }} L/s and a total runoff volume of
    {{ number_with_delimiter(Number(getStormMetric("total_runoff_volume")).toFixed(0)) }} m³ during a
    {{ selectedFrequency }}-year, {{ formattedDuration }} storm event. The stormwater management system must
    accommodate or mitigate this volume through storage, infiltration, or controlled release.
  </p>
</template>

<script setup>
import { ref, computed } from "vue";
import IconListItem from "@/components/IconListItem.vue";
import StormFrequencySelector from "@/components/StormFrequencySelector.vue";
import RainfallDataTables from "@/views/ReportDetailsView/components/RainfallDataTables.vue";
import number_with_delimiter from "@/utilities/numbers";

const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
});

const selectedFrequency = ref(props.report.storm_frequencies[0]?.frequency);
const selectedDuration = ref(props.report.storm_frequencies[0]?.duration);

const formattedDuration = computed(() => {
  const duration = selectedDuration.value;
  if (duration < 60) {
    return `${duration}-minute`;
  } else {
    return `${duration / 60}-hour`;
  }
});

const getStormMetric = (metric) => {
  const key = `storm_${selectedFrequency.value}_${selectedDuration.value}_${metric}`;
  return props.report.metadata[key] || 0;
};
</script>

<style lang="scss" scoped>
ul {
  padding-left: 40px;
}

.calculation-row {
  gap: 20px;
  margin: 20px 0;
}

.operator-sign {
  font-size: 28px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  margin: 0 -5px;
  flex: 0 0 auto;
}

.unit-text {
  display: inline-block;
  line-height: 1;
}
</style>
